<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-col gap-1">
      <titlePrimary :title="isEdit ? $t('property.communityForm.editTitle') : $t('property.communityForm.newTitle')" />
      <p class="text-[#385660] text-sm pt-1">{{ $t('property.communityForm.subtitle') }}</p>
    </div>

    <div v-if="loading" class="text-center p-4">{{ $t('property.detail.loading') }}</div>

    <template v-else>
      <section v-if="selectableAccounts.length > 1 && !isEdit" class="form-section">
        <h2 class="section-title">{{ $t('property.communityForm.sections.account') }}</h2>
        <SelectPrimary :options="ownAccountOptions" :modelValue="selectedAccount?.id ?? ''"
          @update:modelValue="selectOwnAccount" :label="$t('property.communityForm.fields.ownAccount')"
          :error="validationErrors.propietario_id" class="lg:w-1/2" />
      </section>

      <section v-if="isAdsUser && !isEdit" class="form-section">
        <h2 class="section-title">{{ $t('property.communityForm.sections.account') }}</h2>
        <div class="grid lg:grid-cols-2 grid-cols-1 gap-6">
          <div class="relative">
            <inputPrimary v-model="accountSearch" :label="$t('property.communityForm.fields.account')"
              :placeholder="$t('property.communityForm.fields.accountPlaceholder')"
              :error="validationErrors.propietario_id" />
            <div v-if="accountResults.length"
              class="absolute left-0 right-0 top-full mt-1 border rounded p-2 bg-white z-20 max-h-60 overflow-auto shadow">
              <ul>
                <li v-for="account in accountResults" :key="account.id"
                  class="hover:bg-gray-100 cursor-pointer px-2 py-1" @click="selectAccount(account)">
                  <p class="text-[#385660]">{{ account.name }}</p>
                  <p class="text-xs text-gray-500">{{ accountTypeLabel(account, '') }}<span v-if="account.email"> · {{ account.email }}</span></p>
                </li>
              </ul>
            </div>
          </div>
          <div v-if="selectedAccount" class="text-sm text-[#385660] self-end pb-3">
            {{ $t('property.communityForm.fields.selectedAccount') }}: <strong>{{ selectedAccount.name }}</strong>
          </div>
        </div>
      </section>

      <section class="form-section" data-testid="community-data">
        <h2 class="section-title">1. {{ $t('property.communityForm.sections.community') }}</h2>
        <div class="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
          <inputPrimary v-model="form.name" :label="$t('property.communityForm.fields.name')"
            :placeholder="$t('property.communityForm.fields.namePlaceholder')" :error="validationErrors.name" />
          <inputPrimary v-model="form.cif" :label="$t('property.communityForm.fields.document')"
            :placeholder="$t('property.communityForm.fields.documentPlaceholder')" :error="validationErrors.cif" />
          <inputPrimary v-model="form.telephone" type="tel" :label="$t('property.communityForm.fields.telephone')"
            :placeholder="$t('property.communityForm.fields.telephonePlaceholder')" :error="validationErrors.telephone" />
        </div>
      </section>

      <template v-if="!isEdit">
        <section v-for="(block, index) in supplyBlocks" :key="block.uid"
          :id="`community-supply-${index + 1}`" :data-testid="`community-supply-${index + 1}`" class="form-section">
          <div class="flex items-center justify-between gap-4">
            <h2 class="section-title">2. {{ $t('property.communityForm.sections.supply') }} {{ index + 1 }}</h2>
            <button v-if="index > 0" type="button" class="remove-button" @click="removeSupplyBlock(index)">
              {{ $t('property.communityForm.buttons.removeSupply') }}
            </button>
          </div>

          <div class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-6">
            <inputPrimary v-model="block.company" :label="$t('property.communityForm.fields.company')"
              :placeholder="$t('property.communityForm.fields.companyPlaceholder')" :error="blockError(index, 'company')" />
            <inputPrimary v-model="block.iban" :label="$t('property.communityForm.fields.iban')"
              :placeholder="$t('property.communityForm.fields.ibanPlaceholder')" :error="blockError(index, 'iban')" />
            <inputPrimary v-model="block.address" :label="$t('property.communityForm.fields.address')"
              :placeholder="$t('property.communityForm.fields.addressPlaceholder')" :error="blockError(index, 'address')" class="lg:col-span-2" />
            <inputPrimary v-model="block.postal_code" :label="$t('property.communityForm.fields.postalCode')"
              :placeholder="$t('property.communityForm.fields.postalCodePlaceholder')" :error="blockError(index, 'postal_code')" />
            <inputPrimary v-model="block.municipality" :label="$t('property.communityForm.fields.municipality')"
              :placeholder="$t('property.communityForm.fields.municipalityPlaceholder')" :error="blockError(index, 'municipality')" />
            <div class="pt-2 [&_label]:pb-2 [&_select]:w-full [&_select]:max-w-none">
              <SelectPrimary v-model="block.province" :name="`community-province-${index + 1}`" :options="provinces"
                :label="$t('property.communityForm.fields.province')" :placeholder="$t('property.communityForm.fields.provincePlaceholder')"
                :error="blockError(index, 'province')" />
            </div>
            <div></div>
            <inputPrimary v-model="block.cups_light" :label="$t('property.communityForm.fields.cupsLight')"
              :placeholder="$t('property.communityForm.fields.cupsLightPlaceholder')" :error="blockError(index, 'cups_light')" />
            <SelectPrimary v-model="block.light_rate" :name="`community-light-rate-${index + 1}`"
              :options="lightRateOptions(block.light_rate)" :label="$t('property.communityForm.fields.lightRate')"
              :placeholder="$t('property.communityForm.fields.lightRatePlaceholder')" />
            <inputPrimary v-model="block.cups_gas" :label="$t('property.communityForm.fields.cupsGas')"
              :placeholder="$t('property.communityForm.fields.cupsGasPlaceholder')" :error="blockError(index, 'cups_gas')" />
            <SelectPrimary v-model="block.gas_rate" :name="`community-gas-rate-${index + 1}`"
              :options="gasRateOptions(block.gas_rate)" :label="$t('property.communityForm.fields.gasRate')"
              :placeholder="$t('property.communityForm.fields.gasRatePlaceholder')" />
          </div>

          <div class="mt-2">
            <p class="text-[#385660] font-medium">{{ $t('property.communityForm.fields.powers') }}</p>
            <p class="text-xs text-gray-500 mb-3">{{ $t('property.communityForm.fields.powersHint') }}</p>
            <div class="grid lg:grid-cols-6 md:grid-cols-3 grid-cols-2 gap-4">
              <inputPrimary v-for="period in POWER_PERIODS" :key="period" v-model="block[period]"
                type="number" min="0" step="0.01" :label="period.toUpperCase()" :disabled="!powersEnabled(block.light_rate)"
                :error="blockError(index, period)" />
            </div>
          </div>
        </section>

        <div class="flex justify-start">
          <ButtonWhite data-testid="add-community-supply" class="!w-auto" @click="addSupplyBlock">
            <PlusIcon class="w-4 h-4" /> {{ $t('property.communityForm.buttons.addSupply') }}
          </ButtonWhite>
        </div>
      </template>

      <section v-else class="form-section">
        <h2 class="section-title">{{ $t('property.communityForm.sections.location') }}</h2>
        <div class="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
          <inputPrimary v-model="legacy.address" :label="$t('property.communityForm.fields.address')" />
          <inputPrimary v-model="legacy.postal_code" :label="$t('property.communityForm.fields.postalCode')" />
          <inputPrimary v-model="legacy.municipality" :label="$t('property.communityForm.fields.municipality')" />
          <inputPrimary v-model="legacy.province" :label="$t('property.communityForm.fields.province')" />
          <inputPrimary v-model="legacy.company" :label="$t('property.communityForm.fields.company')" />
          <inputPrimary v-model="legacy.iban" :label="$t('property.communityForm.fields.iban')" />
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">3. {{ $t('property.communityForm.sections.additional') }}</h2>
        <div class="grid md:grid-cols-2 grid-cols-1 gap-6">
          <textareaPrimary v-model="form.comments" :label="$t('property.communityForm.fields.comments')"
            :placeholder="$t('property.communityForm.fields.commentsPlaceholder')" :rows="4" />
          <textareaPrimary v-model="form.other" :label="$t('property.communityForm.fields.other')"
            :placeholder="$t('property.communityForm.fields.otherPlaceholder')" :rows="4" />
        </div>
      </section>

      <section class="form-section">
        <h2 class="section-title">4. {{ $t('property.communityForm.sections.documents') }}</h2>
        <div class="grid md:grid-cols-2 grid-cols-1 gap-6">
          <div v-for="slot in FILE_SLOTS" :key="slot">
            <inputPrimary type="file" accept=".jpg,.jpeg,.png,.pdf" class="w-full"
              :label="$t(`property.communityForm.files.${slot}`)" :title="files[slot]?.name || files[slot]?.file_name || ''"
              :placeholder="$t('property.communityForm.files.choose')" @change="handleFileChange($event, slot)" />
            <span v-if="validationErrors[slot]" class="text-red-500 text-xs">{{ validationErrors[slot] }}</span>
            <span v-else-if="files[slot]?.name || files[slot]?.file_name" class="text-sm text-gray-600">
              {{ files[slot]?.name || files[slot]?.file_name }}
            </span>
          </div>
        </div>
        <p class="text-xs text-gray-500">{{ $t('property.communityForm.files.hint') }}</p>
      </section>

      <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>
      <p v-if="recoveryState.propertyId" class="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="community-created-pending-files">
        {{ $t('property.communityForm.validation.createdPendingFiles') }}
      </p>
      <div class="flex flex-col-reverse sm:flex-row justify-end gap-3">
        <ButtonWhite class="sm:!w-40" :disabled="submitting" @click="cancel">
          {{ $t('property.communityForm.buttons.cancel') }}
        </ButtonWhite>
        <ButtonWhite class="sm:!w-52" bgColor="bg-[#25B0D7]" textColor="text-white"
          :disabled="submitting" :class="{ 'opacity-50 cursor-not-allowed': submitting }" @click="submitForm">
          {{ recoveryState.propertyId
            ? $t('property.communityForm.buttons.retryFiles')
            : (isEdit ? $t('property.form.buttons.saveChanges') : $t('property.form.buttons.save')) }}
        </ButtonWhite>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import titlePrimary from '@/components/title/title-primary.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import inputPrimary from '@/components/inputs/input-primary.vue';
import textareaPrimary from '@/components/inputs/textarea-primary.vue';
import SelectPrimary from '@/components/select/SelectPrimary.vue';
import PlusIcon from '@/assets/svg/PlusIcon.vue';
import { isAdsUser as checkIsAdsUser } from '@/services/userRoles';
import { accountTypeLabel, accountTypes } from '@/services/clientAccount';
import { provinceOptions } from '@/constants/provinces';
import { lightRateOptions, gasRateOptions, powersEnabled } from '@/services/estateSupplyRates';
import { POWER_PERIODS, emptyCommunitySupplyBlock, communitySupplyBlocksPayload, validateCommunitySupplyBlocks } from '@/services/communitySupplyBlocks';
import { createCommunityRecoveryState, ensureCommunityCreated, uploadPendingCommunityFiles } from '@/services/communityCreationRecovery';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const id = route.params.id || null;
const isEdit = computed(() => Boolean(id));
const isAdsUser = checkIsAdsUser();
const provinces = provinceOptions();
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const form = reactive({ name: '', cif: '', telephone: '', comments: '', other: '' });
const legacy = reactive({ address: '', postal_code: '', municipality: '', province: '', company: '', iban: '' });
const validationErrors = ref({});
const error = ref('');
const loading = ref(false);
const submitting = ref(false);
const recoveryState = reactive(createCommunityRecoveryState());

let blockUid = 0;
const newBlock = (location = {}) => ({ uid: blockUid += 1, ...emptyCommunitySupplyBlock(location) });
const supplyBlocks = reactive([newBlock()]);
const blockError = (index, field) => validationErrors.value[`supplies.${index}.${field}`] || '';
const clearBlockErrors = () => Object.keys(validationErrors.value)
  .filter((key) => key.startsWith('supplies.')).forEach((key) => delete validationErrors.value[key]);
const addSupplyBlock = () => {
  supplyBlocks.push(newBlock(supplyBlocks[supplyBlocks.length - 1]));
  clearBlockErrors();
  if (typeof document !== 'undefined') nextTick(() => document.getElementById(`community-supply-${supplyBlocks.length}`)?.scrollIntoView({ behavior: 'smooth' }));
};
const removeSupplyBlock = (index) => {
  if (index <= 0 || index >= supplyBlocks.length) return;
  supplyBlocks.splice(index, 1);
  clearBlockErrors();
};
watch(() => supplyBlocks.map((block) => block.light_rate), (rates) => {
  rates.forEach((rate, index) => { if (!rate) POWER_PERIODS.forEach((period) => { supplyBlocks[index][period] = ''; }); });
});

const ownAccounts = ref([]);
const selectedAccount = ref(null);
const selectableAccounts = computed(() => ownAccounts.value.filter((account) => {
  const types = accountTypes(account);
  return types.length === 0 || types.includes('horizontal');
}));
const ownAccountOptions = computed(() => selectableAccounts.value.map((account) => ({
  value: account.id, label: account.type_label ? `${account.name} · ${account.type_label}` : account.name,
})));
const loadOwnAccounts = async () => {
  if (isAdsUser) return;
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/client-account/mine`, { headers: authHeaders() });
    ownAccounts.value = Array.isArray(response.data?.value) ? response.data.value : [];
  } catch { ownAccounts.value = []; }
  if (selectableAccounts.value.length === 1) selectedAccount.value = selectableAccounts.value[0];
};
const selectOwnAccount = (value) => {
  selectedAccount.value = selectableAccounts.value.find((account) => String(account.id) === String(value)) || null;
  validationErrors.value.propietario_id = '';
};

const accountSearch = ref('');
const accountResults = ref([]);
let accountTimeout = null;
let skipNextSearch = false;
watch(accountSearch, (query) => {
  if (skipNextSearch) { skipNextSearch = false; return; }
  if (accountTimeout) clearTimeout(accountTimeout);
  if (!query || query.trim().length < 2) { accountResults.value = []; return; }
  accountTimeout = setTimeout(async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/client-account/search`, {
        params: { query: query.trim(), per_page: 10 }, headers: authHeaders(),
      });
      accountResults.value = (response.data?.value?.data || []).filter((account) => {
        const types = accountTypes(account);
        return types.length === 0 || types.includes('horizontal');
      });
    } catch { accountResults.value = []; }
  }, 300);
});
const selectAccount = (account) => {
  selectedAccount.value = account;
  skipNextSearch = true;
  accountSearch.value = account.name;
  accountResults.value = [];
  validationErrors.value.propietario_id = '';
};

const FILE_SLOTS = ['owner_document', 'property_document'];
const files = reactive({ owner_document: null, property_document: null });
const handleFileChange = (event, slot) => {
  const file = event.target.files?.[0];
  if (!file) return;
  validationErrors.value[slot] = '';
  if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
    validationErrors.value[slot] = t('property.communityForm.validation.fileType'); return;
  }
  if (file.size > 5 * 1024 * 1024) {
    validationErrors.value[slot] = t('property.communityForm.validation.fileSize'); return;
  }
  files[slot] = file;
};
const uploadFiles = async (propertyId) => {
  await uploadPendingCommunityFiles({
    state: recoveryState,
    propertyId,
    files,
    slots: FILE_SLOTS,
    isUploadable: (file) => file instanceof File,
    upload: async (targetId, slot, file) => {
      const payload = new FormData();
      payload.append('file', file);
      payload.append('file_type', slot);
      await axios.post(`${import.meta.env.VITE_API_URL}/property/upload/${targetId}`, payload, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
      });
    },
  });
};

const clean = (value) => String(value || '').trim() || null;
const validate = () => {
  validationErrors.value = {};
  if (!form.name.trim()) validationErrors.value.name = t('property.communityForm.validation.required');
  if (!/^[A-Za-z0-9]{8,10}$/.test(form.cif.trim())) validationErrors.value.cif = t('property.communityForm.validation.document');
  if (form.telephone.trim() && !/^\d{8,15}$/.test(form.telephone.trim())) validationErrors.value.telephone = t('property.communityForm.validation.telephone');
  if (!isEdit.value && !selectedAccount.value && (isAdsUser || selectableAccounts.value.length > 1)) {
    validationErrors.value.propietario_id = t('property.communityForm.validation.account');
  }
  if (!isEdit.value) {
    Object.entries(validateCommunitySupplyBlocks(supplyBlocks)).forEach(([field, key]) => { validationErrors.value[field] = t(key); });
  }
  return Object.keys(validationErrors.value).length === 0;
};

const submitForm = async () => {
  error.value = '';
  if (!recoveryState.propertyId && !validate()) { error.value = t('property.communityForm.validation.review'); return; }
  submitting.value = true;
  try {
    const payload = {
      type: 'comunidad', name: clean(form.name), cif: form.cif.trim().toUpperCase(), telephone: clean(form.telephone),
      comments: clean(form.comments), other: clean(form.other),
    };
    if (selectedAccount.value) payload.propietario_id = selectedAccount.value.id;
    if (isEdit.value) {
      Object.assign(payload, Object.fromEntries(Object.entries(legacy).map(([key, value]) => [key, key === 'iban' && value ? value.replace(/\s+/g, '').toUpperCase() : clean(value)])));
      await axios.put(`${import.meta.env.VITE_API_URL}/property/update/${id}`, payload, { headers: authHeaders() });
      await uploadFiles(id);
      await router.push(`/Property-detail/${id}`);
    } else {
      payload.supplies = communitySupplyBlocksPayload(supplyBlocks);
      const propertyId = await ensureCommunityCreated(
        recoveryState,
        payload,
        (communityPayload) => axios.post(`${import.meta.env.VITE_API_URL}/property/create`, communityPayload, { headers: authHeaders() }),
      );
      await uploadFiles(propertyId);
      await router.push(`/Property-detail/${propertyId}`);
    }
  } catch (err) {
    if (!isEdit.value && recoveryState.propertyId) {
      error.value = t('property.communityForm.validation.documentsPending');
    } else if (err.response?.status === 422 && err.response.data?.errors) {
      Object.entries(err.response.data.errors).forEach(([field, messages]) => { validationErrors.value[field] = messages[0]; });
      error.value = t('property.communityForm.validation.review');
    } else error.value = err.response?.data?.value || t('property.communityForm.validation.saveError');
  } finally { submitting.value = false; }
};

const fetchCommunity = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/property/get/${id}`, { headers: authHeaders() });
    const data = response.data?.value || {};
    Object.keys(form).forEach((key) => { form[key] = data[key] ?? ''; });
    Object.keys(legacy).forEach((key) => { legacy[key] = data[key] ?? ''; });
    (data.files || []).forEach((file) => {
      if (FILE_SLOTS.includes(file.file_type) && !files[file.file_type]) files[file.file_type] = file;
    });
  } catch { error.value = t('property.communityForm.validation.loadError'); }
  finally { loading.value = false; }
};

const cancel = () => router.back();
onMounted(() => { if (isEdit.value) fetchCommunity(); });
onMounted(loadOwnAccounts);
</script>

<style scoped>
.form-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: white;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
}
.section-title { color: var(--c-primary); font-weight: 600; }
.remove-button { color: #385660; cursor: pointer; font-size: 0.875rem; transition: color 0.2s; }
.remove-button:hover { color: #ef4444; }
</style>
